import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../supabaseClient';

// ── Helpers ────────────────────────────────────────────────────────────────────

// Each library video is stored as JSON: { title, url }
function parseVideo(row) {
  try {
    const parsed = JSON.parse(row.value);
    return { key: row.key, title: parsed.title ?? '', url: parsed.url ?? '' };
  } catch {
    // Fallback if value isn't JSON
    return { key: row.key, title: row.key, url: row.value ?? '' };
  }
}

function urlPreview(url) {
  if (!url) return '—';
  const stripped = url.replace(/^https?:\/\//, '');
  return stripped.length > 52 ? stripped.slice(0, 52) + '…' : stripped;
}

function nextKey(videos) {
  const nums = videos
    .map(v => parseInt(v.key.replace('video_', ''), 10))
    .filter(n => !isNaN(n));
  return `video_${nums.length > 0 ? Math.max(...nums) + 1 : 1}`;
}

// ── Inline form (add & edit) ───────────────────────────────────────────────────

const EMPTY_FORM = { title: '', url: '' };

function VideoForm({ initial = EMPTY_FORM, onSave, onCancel, saving, saveStatus }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inputStyle = {
    backgroundColor: '#2A2A2A',
    border: '1px solid rgba(255,255,255,0.1)',
  };

  return (
    <div
      className="rounded-2xl flex flex-col gap-5 p-6 mt-2"
      style={{ backgroundColor: '#252525', border: '1px solid rgba(247,148,29,0.25)' }}
    >
      {/* Title */}
      <div className="flex flex-col gap-1.5">
        <label className="font-poppins font-semibold text-white text-sm">Title</label>
        <p className="font-poppins text-white/40 text-xs -mt-0.5">
          A name to help you identify this video.
        </p>
        <input
          type="text"
          value={form.title}
          onChange={e => set('title', e.target.value)}
          placeholder="e.g. Summer Bowl 2026"
          className="font-poppins text-sm text-white placeholder-white/20 rounded-xl px-4 py-3 focus:outline-none transition-colors duration-200"
          style={inputStyle}
          onFocus={e => e.target.style.borderColor = 'rgba(247,148,29,0.5)'}
          onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
        />
      </div>

      {/* URL */}
      <div className="flex flex-col gap-1.5">
        <label className="font-poppins font-semibold text-white text-sm">Video URL</label>
        <p className="font-poppins text-white/40 text-xs -mt-0.5">
          Paste a YouTube or Vimeo URL here.
        </p>
        <input
          type="text"
          value={form.url}
          onChange={e => set('url', e.target.value)}
          placeholder="https://www.youtube.com/watch?v=..."
          className="font-poppins text-sm text-white placeholder-white/20 rounded-xl px-4 py-3 focus:outline-none transition-colors duration-200"
          style={inputStyle}
          onFocus={e => e.target.style.borderColor = 'rgba(247,148,29,0.5)'}
          onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
        />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-1">
        <button
          type="button"
          onClick={() => onSave(form)}
          disabled={saving}
          className="font-poppins font-bold text-sm px-6 py-2.5 rounded-xl transition-all duration-200 focus:outline-none"
          style={{
            backgroundColor: saving ? 'rgba(247,148,29,0.4)' : '#F7941D',
            color: '#1A1A1A',
            cursor: saving ? 'not-allowed' : 'pointer',
          }}
          onMouseEnter={e => { if (!saving) e.currentTarget.style.backgroundColor = '#ffb83f'; }}
          onMouseLeave={e => { if (!saving) e.currentTarget.style.backgroundColor = '#F7941D'; }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="font-poppins font-medium text-sm px-6 py-2.5 rounded-xl transition-all duration-200 focus:outline-none"
          style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
        >
          Cancel
        </button>

        {saveStatus === 'success' && (
          <div className="flex items-center gap-1.5">
            <span style={{ color: '#4ade80' }}>✓</span>
            <p className="font-poppins text-sm font-medium" style={{ color: '#4ade80' }}>Saved!</p>
          </div>
        )}
        {saveStatus === 'error' && (
          <p className="font-poppins text-sm font-medium" style={{ color: '#f87171' }}>
            Please fill in both fields.
          </p>
        )}
      </div>
    </div>
  );
}

// ── Video card ─────────────────────────────────────────────────────────────────

function VideoCard({ video, isActive, onSetActive, onEdit, onDelete, settingActive }) {
  return (
    <div
      className="rounded-2xl p-5 flex flex-col gap-3 transition-all duration-200"
      style={{
        backgroundColor: '#202020',
        border: isActive ? '1px solid rgba(247,148,29,0.45)' : '1px solid rgba(255,255,255,0.08)',
      }}
    >
      {/* Title row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5 min-w-0">
          <p className="font-poppins font-bold text-white text-base truncate">
            {video.title || <span style={{ color: 'rgba(255,255,255,0.3)' }}>Untitled</span>}
          </p>
          <p className="font-poppins text-xs truncate" style={{ color: 'rgba(255,255,255,0.3)' }}>
            {urlPreview(video.url)}
          </p>
        </div>
      </div>

      {/* Action buttons */}
      <div
        className="flex items-center gap-2 flex-wrap pt-1"
        style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
      >
        {/* Set as Active */}
        {!isActive && (
          <button
            type="button"
            onClick={() => onSetActive(video)}
            disabled={settingActive}
            className="font-poppins text-xs font-semibold px-4 py-2.5 rounded-lg transition-all duration-200 focus:outline-none"
            style={{
              backgroundColor: 'rgba(247,148,29,0.1)',
              color: '#F7941D',
              border: '1px solid rgba(247,148,29,0.2)',
              cursor: settingActive ? 'not-allowed' : 'pointer',
              opacity: settingActive ? 0.5 : 1,
            }}
            onMouseEnter={e => { if (!settingActive) e.currentTarget.style.backgroundColor = 'rgba(247,148,29,0.2)'; }}
            onMouseLeave={e => { if (!settingActive) e.currentTarget.style.backgroundColor = 'rgba(247,148,29,0.1)'; }}
          >
            Set as Active
          </button>
        )}
        {isActive && (
          <span
            className="font-poppins text-xs font-semibold px-4 py-2.5 rounded-lg"
            style={{
              backgroundColor: 'rgba(247,148,29,0.07)',
              color: 'rgba(247,148,29,0.5)',
              border: '1px solid rgba(247,148,29,0.12)',
            }}
          >
            ✓ Showing on site
          </span>
        )}

        {/* Edit */}
        <button
          type="button"
          onClick={() => onEdit(video)}
          className="font-poppins text-xs font-semibold px-4 py-2.5 rounded-lg transition-all duration-200 focus:outline-none"
          style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.1)' }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
        >
          Edit
        </button>

        {/* Delete */}
        <button
          type="button"
          onClick={() => onDelete(video)}
          className="font-poppins text-xs font-semibold px-4 py-2.5 rounded-lg transition-all duration-200 focus:outline-none"
          style={{ backgroundColor: 'rgba(248,113,113,0.08)', color: 'rgba(248,113,113,0.7)', border: '1px solid rgba(248,113,113,0.15)' }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(248,113,113,0.18)'; e.currentTarget.style.color = '#f87171'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(248,113,113,0.08)'; e.currentTarget.style.color = 'rgba(248,113,113,0.7)'; }}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

// ── Delete confirmation modal ──────────────────────────────────────────────────

function DeleteModal({ video, isActive, onConfirm, onCancel, deleting }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}
    >
      <div
        className="rounded-2xl p-7 flex flex-col gap-5 w-full max-w-sm"
        style={{ backgroundColor: '#202020', border: '1px solid rgba(255,255,255,0.1)' }}
      >
        <div className="flex flex-col gap-2">
          <h2 className="font-poppins font-bold text-white text-lg">Delete video?</h2>
          <p className="font-poppins text-white/50 text-sm leading-relaxed">
            You're about to delete{' '}
            <span className="text-white font-semibold">"{video?.title || 'this video'}"</span>.
          </p>
          {isActive && (
            <p
              className="font-poppins text-sm leading-relaxed px-4 py-3 rounded-xl"
              style={{ backgroundColor: 'rgba(248,113,113,0.08)', color: '#f87171', border: '1px solid rgba(248,113,113,0.2)' }}
            >
              ⚠️ This is the currently active video. Deleting it will clear the homepage video.
            </p>
          )}
          <p className="font-poppins text-white/40 text-sm">This cannot be undone.</p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="font-poppins font-bold text-sm px-6 py-2.5 rounded-xl focus:outline-none transition-all duration-200"
            style={{
              backgroundColor: deleting ? 'rgba(248,113,113,0.4)' : '#f87171',
              color: '#fff',
              cursor: deleting ? 'not-allowed' : 'pointer',
            }}
          >
            {deleting ? 'Deleting…' : 'Yes, delete it'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="font-poppins font-medium text-sm px-6 py-2.5 rounded-xl focus:outline-none transition-all duration-200"
            style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main section ───────────────────────────────────────────────────────────────

function VideoSection() {
  const [videos, setVideos]           = useState([]);
  const [activeUrl, setActiveUrl]     = useState('');
  const [loading, setLoading]         = useState(true);
  const [editingKey, setEditingKey]   = useState(null); // null | 'new' | key string
  const [editInitial, setEditInitial] = useState(EMPTY_FORM);
  const [saving, setSaving]           = useState(false);
  const [saveStatus, setSaveStatus]   = useState(null);
  const [settingActive, setSettingActive] = useState(false);
  const [deleteTarget, setDeleteTarget]   = useState(null);
  const [deleting, setDeleting]           = useState(false);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('settings')
      .select('key, value')
      .like('key', 'video%');

    const rows = data || [];
    const activeRow = rows.find(r => r.key === 'video_url');
    const libraryRows = rows
      .filter(r => r.key !== 'video_url' && /^video_\d+$/.test(r.key))
      .map(parseVideo)
      .sort((a, b) => {
        const na = parseInt(a.key.replace('video_', ''), 10);
        const nb = parseInt(b.key.replace('video_', ''), 10);
        return na - nb;
      });

    setActiveUrl(activeRow?.value ?? '');
    setVideos(libraryRows);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Set as active ──────────────────────────────────────────────────────────
  async function handleSetActive(video) {
    setSettingActive(true);
    await supabase
      .from('settings')
      .update({ value: video.url })
      .eq('key', 'video_url');
    setActiveUrl(video.url);
    setSettingActive(false);
  }

  // ── Open forms ─────────────────────────────────────────────────────────────
  function handleEdit(video) {
    setEditInitial({ title: video.title, url: video.url });
    setEditingKey(video.key);
    setSaveStatus(null);
  }

  function handleAddNew() {
    setEditInitial(EMPTY_FORM);
    setEditingKey('new');
    setSaveStatus(null);
  }

  function handleCancel() {
    setEditingKey(null);
    setSaveStatus(null);
  }

  // ── Save ───────────────────────────────────────────────────────────────────
  async function handleSave(form) {
    if (!form.title.trim() || !form.url.trim()) {
      setSaveStatus('error');
      return;
    }
    setSaving(true);
    setSaveStatus(null);

    const value = JSON.stringify({ title: form.title.trim(), url: form.url.trim() });
    let error;

    if (editingKey === 'new') {
      const key = nextKey(videos);
      ({ error } = await supabase.from('settings').insert({ key, value }));
    } else {
      ({ error } = await supabase.from('settings').update({ value }).eq('key', editingKey));
      // If this was the active video, update video_url too so the site stays in sync
      const edited = videos.find(v => v.key === editingKey);
      if (!error && edited && edited.url === activeUrl) {
        await supabase.from('settings').update({ value: form.url.trim() }).eq('key', 'video_url');
        setActiveUrl(form.url.trim());
      }
    }

    setSaving(false);
    if (error) {
      setSaveStatus('error');
    } else {
      setSaveStatus('success');
      await fetchAll();
      setTimeout(() => { setEditingKey(null); setSaveStatus(null); }, 1200);
    }
  }

  // ── Delete ─────────────────────────────────────────────────────────────────
  async function handleDeleteConfirm() {
    setDeleting(true);
    const { error } = await supabase
      .from('settings')
      .delete()
      .eq('key', deleteTarget.key);

    if (!error) {
      // If this was the active video, clear video_url
      if (deleteTarget.url === activeUrl) {
        await supabase.from('settings').update({ value: '' }).eq('key', 'video_url');
        setActiveUrl('');
      }
      setVideos(prev => prev.filter(v => v.key !== deleteTarget.key));
      if (editingKey === deleteTarget.key) setEditingKey(null);
    }
    setDeleting(false);
    setDeleteTarget(null);
  }

  // ── Loading skeleton ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col gap-6 animate-pulse">
        <div className="h-7 w-36 rounded-lg bg-white/10" />
        {[1, 2].map(i => <div key={i} className="h-28 rounded-2xl bg-white/5" />)}
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-6 max-w-2xl">

      {/* Header + Add button */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-poppins font-bold text-white text-2xl">Video Library</h1>
          <p className="font-poppins text-white/40 text-sm mt-1">
            Save multiple videos and choose which one plays on your homepage.
          </p>
        </div>
        {editingKey !== 'new' && (
          <button
            type="button"
            onClick={handleAddNew}
            className="font-poppins font-bold text-sm px-5 py-2.5 rounded-xl transition-all duration-200 focus:outline-none flex items-center gap-2 flex-shrink-0"
            style={{ backgroundColor: '#F7941D', color: '#1A1A1A' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#ffb83f'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#F7941D'}
          >
            <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
            Add Video
          </button>
        )}
      </div>

      {/* Add new form */}
      {editingKey === 'new' && (
        <VideoForm
          initial={editInitial}
          onSave={handleSave}
          onCancel={handleCancel}
          saving={saving}
          saveStatus={saveStatus}
        />
      )}

      {/* Empty state */}
      {videos.length === 0 && editingKey !== 'new' && (
        <div
          className="rounded-2xl p-10 flex flex-col items-center justify-center gap-3"
          style={{ backgroundColor: '#202020', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <p className="font-poppins text-white/30 text-sm text-center">
            No videos yet. Hit "Add Video" to get started!
          </p>
        </div>
      )}

      {/* Video list */}
      {videos.length > 0 && (
        <div className="flex flex-col gap-3">
          {videos.map(video => (
            <div key={video.key}>
              <VideoCard
                video={video}
                isActive={video.url === activeUrl && activeUrl !== ''}
                onSetActive={handleSetActive}
                onEdit={handleEdit}
                onDelete={v => setDeleteTarget(v)}
                settingActive={settingActive}
              />
              {editingKey === video.key && (
                <VideoForm
                  initial={editInitial}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  saving={saving}
                  saveStatus={saveStatus}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Delete modal */}
      {deleteTarget && (
        <DeleteModal
          video={deleteTarget}
          isActive={deleteTarget.url === activeUrl && activeUrl !== ''}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
          deleting={deleting}
        />
      )}

    </div>
  );
}

export default VideoSection;
